import React from "react";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType, Module, } from "../../../assets/types/common";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { LoadingIndicator } from "../../../assets/vfx/loadingIndicator/vfxLoadingIndicator";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { ViewProps } from "../props";
import { LookupValueForm as EditComponent } from "./lookupValueForm";
import { LookupValuePage } from "./lookupValuePage";
import { LookupValueService } from "./lookupValueService";
import { LookupValueView as LookupValueViewComponenet } from "./lookupValueView";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";


interface LookupValueProps<T> {
    moduleType: Module;
}

export const LookupValueContainer = <T extends ViewProps.ValueTypeDto>(
    props: React.PropsWithoutRef<LookupValueProps<T>>
) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const [data, setData] = React.useState<T>();
    const [drawerType, setDrawerType] = React.useState<GridActionType>(
        GRID_ACTION_TYPES.HIDDEN
    );
    const service: LookupValueService<T> = new LookupValueService<T>(
        props.moduleType
    );
    const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);
    const lookupValueQuery = service.getLookupValueQuery(data?.id ?? undefined);

    const lookupQuery = service.getLookupTypesAsLookupQuery();

    function EditRecord(data: any) {
        setData(data);
        setDrawerType(GRID_ACTION_TYPES.EDIT);
        setSearchAction(GRID_ACTION_TYPES.EDIT, data?.id);
    }

    function OnCustomEvent(actionType: string, dataId: string) {
        if (isGridActionType(actionType)) {
            setDrawerType(actionType);
            setData({ id: dataId } as T);
            setSearchAction(actionType, dataId);
        }
    }

    const { status, mutateAsync: mutate } = service.save();
    const { status: deactivatestatus, mutate: deactivateMutate } =
        service.deActivate();

    const handleOnSubmit = async (values: T) => {
        const isEdit = data && data.id && data.id !== Constants.emptyGuid;
        values.id = isEdit ? data.id : Constants.emptyGuid;
        return await mutate(values, { onSuccess: (response: IActionResult<T>) => handleResponse(response, "Lookup Value Saved") });
    };

    const closeDrawer = () => {
        setDrawerType(GRID_ACTION_TYPES.HIDDEN);
        clearSearchAction();
    }

    const handleDelete = () => deactivateMutate(data?.id ?? Constants.emptyGuid, { onSuccess: (response: IActionResponse) => handleResponse(response, "Lookup Value Deleted") });

    const renderDrawerContent = () => {
        switch (drawerType) {
            case GRID_ACTION_TYPES.NEW: {
                return (
                    <EditComponent<T>
                        show={true}
                        onHide={closeDrawer}
                        onSave={(data) => handleOnSubmit(data)}
                        lookupCategoryLookupValues={lookupQuery?.data}
                        moduleType={props.moduleType}
                        isLoading={lookupQuery?.isLoading}
                    />
                );
            }
            case GRID_ACTION_TYPES.EDIT:
                return (
                    <EditComponent<T>
                        data={lookupValueQuery?.data}
                        show={!!lookupValueQuery?.data}
                        onHide={closeDrawer}
                        onSave={(data) => handleOnSubmit(data)}
                        lookupCategoryLookupValues={lookupQuery?.data}
                        moduleType={props.moduleType}
                        isLoading={lookupQuery?.isLoading || lookupValueQuery?.isLoading}
                    />
                );
            case GRID_ACTION_TYPES.VIEW:
            case GRID_ACTION_TYPES.DOUBLE_CLICK:
                return (
                    <LookupValueViewComponenet<T>
                        data={lookupValueQuery?.data}
                        show={!!lookupValueQuery?.data}
                        lookupCategoryLookupValues={lookupQuery?.data}
                        moduleType={props.moduleType}
                        onHide={closeDrawer}
                        onDelete={handleDelete}
                        onEdit={() => EditRecord(lookupValueQuery.data)}
                        isLoading={lookupQuery?.isLoading || lookupValueQuery?.isLoading}
                    />
                );
        }
    };

    function handleResponse(response: IActionResponse, snackbarMessage: string) {
        if (response) {
            if (response.isSuccess) {
                showSnackbar(snackbarMessage, MessageSeverity.Success);
                lookupValueQuery.refetch();
                queryClient.invalidateQueries({ queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.Grid]] });
                closeDrawer();
            } else {
                if (response.errorMessages) {
                    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
                    snackbarMessage = errorMsg ?? "Error";
                }
                showSnackbar(snackbarMessage, MessageSeverity.Error);
            }
        }
        else {
            showSnackbar("Error, unable to process your request please try again later.", MessageSeverity.Error);
        }
    }

    return (
        <div>
            {lookupQuery.isLoading ? (
                <LoadingIndicator isFullPage={true} />
            ) : (
                <LookupValuePage<T>
                    onCustomEvent={OnCustomEvent}
                    moduleType={props.moduleType}
                    lookupCategoryLookupValues={lookupQuery?.data}
                />
            )}
            {renderDrawerContent()}
        </div>
    );
};
